import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, MessageSquare, Eye, ShieldCheck, Users, FileCheck2, Route,
  Layers, Ban, Palette, Globe, Building2, LifeBuoy, Send, CornerDownRight, Server,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import { Button } from "../components/ui/button";

const FLOW = [
  { icon: MessageSquare, step: "Customer asks", desc: "A visitor or customer chats with Vör on your site, app or portal." },
  { icon: Route, step: "Bifröst relay", desc: "The conversation is routed through norse3 rather than straight to a model." },
  { icon: ShieldCheck, step: "Aegis policy", desc: "Each message is checked against your organisation's policies in real time." },
  { icon: Ban, step: "Allow · warn · redact · block · escalate", desc: "Configured actions apply before a response is delivered." },
  { icon: FileCheck2, step: "Heimdall evidence", desc: "The event, policy, action and any human decision are retained for review." },
];

const FEATURES = [
  { icon: ShieldCheck, t: "Policy in real time", d: "Every message and response is evaluated against your configured, versioned policies before delivery." },
  { icon: Eye, t: "Sensitive-data awareness", d: "Detect and redact designated information so it isn't echoed back or stored in the clear." },
  { icon: Users, t: "Human escalation", d: "Hand off to a named reviewer when a conversation needs a person, not a model." },
  { icon: FileCheck2, t: "Reviewable evidence", d: "A linked, time-stamped trail of what happened — useful for QA and compliance teams." },
  { icon: Layers, t: "Model-independent", d: "Vör governs the interaction and stays independent of the underlying model or vendor." },
  { icon: Palette, t: "On-brand", d: "Match your colours, tone and greeting so Vör feels like part of your product." },
];

const PLACES = [
  { icon: Globe, t: "Public website", d: "Answer questions and guide visitors with oversight in place." },
  { icon: Building2, t: "In-app assistant", d: "Support signed-in users inside your product." },
  { icon: LifeBuoy, t: "Help centre", d: "Deflect routine queries while keeping risky ones supervised." },
  { icon: Users, t: "Customer portal", d: "Assist account holders within a governed boundary." },
];

function VorChatMock() {
  return (
    <div className="relative" data-testid="vor-chat-mock">
      <div className="rounded-lg border border-white/10 bg-navy-900/80 shadow-2xl shadow-black/40 overflow-hidden">
        <div className="flex items-center gap-2.5 border-b border-white/10 bg-navy-800/70 px-4 py-3">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary"><MessageSquare className="h-4 w-4" /></span>
          <div>
            <p className="text-sm font-semibold text-foreground">Vör</p>
            <p className="text-[0.7rem] text-signal-green">Online · governed by norse3</p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="max-w-[85%] rounded-lg rounded-bl-sm bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-foreground">
            Hi! I'm Vör. How can I help you today?
          </div>
          <div className="ml-auto max-w-[80%] rounded-lg rounded-br-sm bg-primary/12 border border-primary/25 px-4 py-2.5 text-sm text-foreground">
            Can you recommend which investment I should buy?
          </div>
          <div className="max-w-[88%] rounded-lg rounded-bl-sm bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-foreground">
            I can share general information, but I can't give personal investment advice. I'll connect you with a qualified adviser.
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-signal-amber/30 bg-signal-amber/[0.07] px-3 py-2 text-xs text-signal-amber">
            <CornerDownRight className="h-3.5 w-3.5" /> Policy: financial advice · escalated to a human reviewer
          </div>
          <div className="mt-1 flex items-center gap-2 rounded-full border border-white/10 bg-navy-800 px-3 py-2">
            <input disabled placeholder="Write a message…" className="flex-1 bg-transparent text-sm text-muted-foreground outline-none" />
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground"><Send className="h-3.5 w-3.5" /></span>
          </div>
        </div>
      </div>
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] bg-primary/10 blur-3xl" aria-hidden="true" />
    </div>
  );
}

export default function Vor() {
  useEffect(() => { document.title = "V\u00f6r | norse3"; }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-14 lg:pt-36 lg:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div className="max-w-2xl animate-fade-up">
              <Eyebrow>Vör · watchful awareness</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-[1.06] text-foreground text-balance">
                A chat assistant your customers can use — and your firm can stand behind.
              </h1>
              <p className="mt-4 text-base font-semibold text-primary">In plain English: an AI chat for your customers that follows your rules and hands over to a human when it matters.</p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Vör is a governed AI chat widget for your website, app or portal. Every message is
                checked against your policies in real time, risky conversations are escalated to a
                person, and each interaction leaves a reviewable evidence trail — so customers get
                help while your firm stays in control.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/book-a-demo" data-testid="vor-hero-cta">Discuss a Vör pilot <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                  <Link to="/interactive-demo">See it in action</Link>
                </Button>
              </div>
            </div>
            <div className="animate-fade-up lg:pl-4" style={{ animationDelay: "120ms" }}>
              <VorChatMock />
            </div>
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section className="border-b border-white/10 py-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: ShieldCheck, t: "Policy in real time", d: "Checked before every reply" },
              { icon: Users, t: "Human escalation", d: "A person takes over when it matters" },
              { icon: FileCheck2, t: "Reviewable evidence", d: "A trail for QA and compliance" },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/60 p-5">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{x.t}</p>
                    <p className="text-sm text-muted-foreground">{x.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The problem"
              title="Customers want fast answers. Your firm can't hand over the controls."
              subtitle="An unconstrained chatbot can give unsuitable answers, leak information or handle a vulnerable customer badly — and leave you with no record of what happened. Native model guardrails aren't your policy."
            />
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="How Vör works"
              title="Ask → relay → policy → act → evidence."
              subtitle="The same control and evidence path as the rest of the norse3 layer, wrapped in a chat experience your customers recognise."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-5 sm:grid-cols-2">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.step} delay={i * 80}>
                  <div className="relative h-full rounded-xl border border-white/10 bg-navy-900/60 p-5">
                    <span className="absolute right-4 top-4 text-xs font-mono text-primary/50">0{i + 1}</span>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{f.step}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="What you get"
              title="A helpful assistant, kept on a governed leash."
              subtitle="Vör is designed to support alignment with your policies. It is not a compliance replacement, and your firm remains accountable."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((x, i) => {
              const Icon = x.icon;
              return (
                <Reveal key={x.t} delay={i * 60}>
                  <div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Where it fits */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Where Vör fits"
              title="Wherever your customers already talk to you."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PLACES.map((x, i) => {
              const Icon = x.icon;
              return (
                <Reveal key={x.t} delay={i * 60}>
                  <div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Evidence + Deployment */}
      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <SectionHeading eyebrow="Evidence" title="A record behind every conversation." subtitle="Where configured, Vör retains a reviewable trail of what was asked, the policy applied, the action taken and any human decision." />
            <ul className="mt-6 space-y-2.5">
              {["Interaction reference and risk classification", "Policy applied and action (allow / warn / redact / block / escalate)", "Human escalation and reviewer decision", "Reference retained — not the raw sensitive content"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground"><FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{t}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <SectionHeading eyebrow="Deployment" title="Drops into your product." subtitle="Embed on your site, app, help centre or customer portal." />
            <div className="mt-6 grid gap-3">
              {[["Hosted or private cloud", "Chosen against sensitivity and data residency."], ["Model-independent", "Governs the interaction, whichever model sits behind it."], ["On-brand", "Match your colours, tone and greeting."]].map(([t, d]) => (
                <div key={t} className="rounded-xl border border-white/10 bg-navy-900/60 p-4 flex items-start gap-3">
                  <Server className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div><p className="text-sm font-semibold text-foreground">{t}</p><p className="text-sm text-muted-foreground">{d}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="FAQ" title="Common questions." /></Reveal>
          <div className="mt-8 space-y-3">
            {[
              ["Does Vör replace our AI model?", "No. Vör is an independent control layer in front of the model. The model stays separate — Vör controls what it can see, say and do."],
              ["Can it stop the AI giving advice it shouldn't?", "Yes, where configured. Responses can be held, redacted or escalated to a person before they reach the customer."],
              ["What happens with a vulnerable customer?", "Vör can detect indicators and escalate the conversation to a named reviewer under your policy."],
              ["Do you store conversation content?", "Retention is configurable; Vör can operate on a minimal-evidence basis, keeping a reference rather than raw content."],
            ].map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground flex items-center justify-between">{q}<ArrowDown className="h-4 w-4 text-primary transition-transform group-open:rotate-180" /></summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-lg border border-white/10 bg-gradient-to-b from-navy-800/60 to-navy-900/60 p-8 lg:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-balance">Put a governed assistant in front of your customers.</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">See how Vör applies your policies, escalates to people and captures evidence — in a 30-minute walkthrough with our team.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                <Link to="/skjold">See Skjold</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
